// src/components/AllRisks.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { usePageSize } from '../hooks/usePageSize';
import { useAuth } from '../contexts/AuthContext';
import { useHasLicense } from '../contexts/LicenseContext';
import Navigation from './Navigation';
import { PINNED_PANEL_TOP } from './PinnedNavigation';
import LoadingSpinner from './LoadingSpinner';
import Pagination from './Pagination';
import Card from './Card';
import RiskDetailTabs, { formatImpact } from './RiskDetailTabs';
import { Search, ChevronDown, ChevronRight, Filter, Shield } from 'lucide-react';
import BulkAssignRiskModal from './BulkAssignRiskModal';
import RiskWorkflowActions from './RiskWorkflowActions';
import { useRiskWorkflow } from '../hooks/useRiskWorkflow';
import { assignedUserIdOf } from '../utils/riskWorkflow';
import { parseCategories } from './remediationScorecardDrillThrough';

// Every URL param a Remediation Scorecard drill-through link sets (see buildDrillThroughUrl).
const DRILL_PARAMS = ['types', 'controlSource', 'category', 'scorecardCategory', 'riskType', 'blankControlSource', 'blankRiskType'];

// Affected-object names live in an "Affected <label>: [...]" description line (no structured
// column yet) — parser and label map are shared in utils/affectedResources.js and unit-tested there.

const AllRisks = () => {
    const apiCall = useApi();
    const { user } = useAuth();
    const hasLicense = useHasLicense();
    // Who may do what to a risk: the same rules as every other page that works a risk (useRiskWorkflow).
    const { canManageOrgRisks, canManageRisk } = useRiskWorkflow();
    const [searchParams, setSearchParams] = useSearchParams();

    const allFilterTypes = ['Deals', 'BusinessUnits', 'Enterprise', 'TPRM', 'Ransomware', 'DarkWeb', 'AI', 'GRC', 'External'];

    const licensedFilterTypes = useMemo(() =>
        [...allFilterTypes.filter(type => type === 'External' || hasLicense(type))], [hasLicense]);

    const getInitialFilterTypes = () => {
        const typesParam = searchParams.get('types');
        if (typesParam) {
            const selectedTypes = typesParam.split(',');
            return {
                Deals: selectedTypes.includes('Deals') || selectedTypes.includes('Application'),
                Enterprise: selectedTypes.includes('Enterprise'),
                DarkWeb: selectedTypes.includes('DarkWeb'),
                Ransomware: selectedTypes.includes('Ransomware'),
                TPRM: selectedTypes.includes('TPRM'),
                AI: selectedTypes.includes('AI'),
                BusinessUnits: selectedTypes.includes('BusinessUnits'),
                GRC: selectedTypes.includes('GRC'),
                External: selectedTypes.includes('External'),
            };
        }
        return { Deals: true, Enterprise: true, DarkWeb: true, Ransomware: true, TPRM: true, AI: true, BusinessUnits: true, GRC: true, External: true };
    };

    const [pageSize, setPageSize] = usePageSize('risks');
    const listTopRef = useRef(null);
    const [risks, setRisks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterTypes, setFilterTypes] = useState(getInitialFilterTypes);
    // Status/severity can be deep-linked (e.g. the dashboard hero "Active Risks" / "Critical" tiles).
    // Unknown values fall back to 'all' so a bad link never yields an empty, unexplained list.
    const statusOptions = ['active', 'Not Started', 'Planned', 'In Progress', 'Under Review', 'Completed', 'Deferred'];
    const severityOptions = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
    const initialStatusParam = statusOptions.find(s => s.toLowerCase() === (searchParams.get('status') || '').toLowerCase());
    const initialSeverityParam = severityOptions.find(s => s === (searchParams.get('severity') || '').toUpperCase());
    const [filterStatus, setFilterStatus] = useState(() => initialStatusParam || 'all');
    const [filterSeverity, setFilterSeverity] = useState(() => initialSeverityParam || 'all');
    const [filterFramework, setFilterFramework] = useState('all');
    // Deal/BU can be deep-linked (e.g. a "View Risks" button on a dashboard scope card) so the list
    // opens pre-filtered to that entity — surfacing its tagged risks even with no assessment.
    const [filterDeal, setFilterDeal] = useState(() => searchParams.get('deal') || 'all');
    const [filterApplication, setFilterApplication] = useState('all');
    const [filterVendor, setFilterVendor] = useState('all');
    const [filterBusinessUnit, setFilterBusinessUnit] = useState(() => searchParams.get('businessUnit') || 'all');
    // Org-assessment "View Risks" deep link (?scope=org): restrict to ORG-LEVEL risks (no deal, no BU).
    const scopeOrg = searchParams.get('scope') === 'org';
    const [filterBUApplication, setFilterBUApplication] = useState('all');
    const [filterAllApplication, setFilterAllApplication] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('severity');
    const [sortOrder, setSortOrder] = useState('desc');
    const [page, setPage] = useState(1);
    const [filterRemediation, setFilterRemediation] = useState('all'); // 'all' | 'active' | 'remediated'
    const [grcEnrichState, setGrcEnrichState] = useState({ loading: false, message: null, isError: false, queued: false });
    // Open the filter panel when arriving pre-filtered, so the active filters are visible.
    const [filtersExpanded, setFiltersExpanded] = useState(() => !!(initialStatusParam || initialSeverityParam || searchParams.has('category')));
    const [riskActiveTabs, setRiskActiveTabs] = useState({});
    const [expandedCards, setExpandedCards] = useState({});
    const toggleCard = (key) => setExpandedCards(prev => ({ ...prev, [key]: !prev[key] }));
    const [filterControlSource, setFilterControlSource] = useState(() => searchParams.get('controlSource') || 'all');
    const [filterRiskCategory, setFilterRiskCategory] = useState('all');
    const [filterAssignedTo, setFilterAssignedTo] = useState('all');
    // Remediation Scorecard drill-through (?category=...): one row's original categories and source.
    const [drillCategories, setDrillCategories] = useState(() => parseCategories(searchParams));
    const [drillRiskType, setDrillRiskType] = useState(() => searchParams.get('riskType'));
    const [drillBlankControlSource, setDrillBlankControlSource] = useState(() => searchParams.get('blankControlSource') === '1');
    // Mixed-origin source: also keep this risk type when its control source is blank.
    const [drillBlankRiskType, setDrillBlankRiskType] = useState(() => searchParams.get('blankRiskType'));
    const scorecardCategory = searchParams.get('scorecardCategory');
    // Set only by the Phase 1 effect below (from ?riskId=), never seeded from the URL: a seeded
    // value let Phase 2 jump to the card in the same commit as Phase 1's filter reset, clear the
    // highlight, and then the filter change reset the list to page 1 — so "View Risk" links landed
    // on page 1 with the target card off-screen.
    const [highlightRiskId, setHighlightRiskId] = useState(null);
    // A "View Risk" link means that one risk: the list shows only it until "Show all risks" is clicked.
    const [focusRiskId, setFocusRiskId] = useState(() => searchParams.get('riskId') || null);
    const showAllRisks = () => {
        setFocusRiskId(null);
        const next = new URLSearchParams(searchParams);
        next.delete('riskId');
        setSearchParams(next, { replace: true });
    };

    const [users, setUsers] = useState([]);
    const [userMap, setUserMap] = useState({});

    const selectedOrgName = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('selectedOrganization'))?.organizationName || null; }
        catch { return null; }
    }, []);

    const clearDrillStates = () => {
        setDrillCategories(null);
        setDrillRiskType(null);
        setDrillBlankControlSource(false);
        setDrillBlankRiskType(null);
    };

    // A user changed a filter select: the scorecard params go from the URL too, or a refresh would bring the
    // old drill-through back. `controlSource` is rewritten to the new selection (when given) so it survives.
    const dropDrillParams = (controlSource) => {
        if (!searchParams.has('category')) return;
        const next = new URLSearchParams(searchParams);
        ['category', 'scorecardCategory', 'riskType', 'blankControlSource', 'blankRiskType'].forEach(name => next.delete(name));
        if (controlSource !== undefined) {
            if (controlSource === 'all') next.delete('controlSource');
            else next.set('controlSource', controlSource);
        }
        setSearchParams(next, { replace: true });
    };

    // Leaves the drill-through entirely. The params must go too: resetAllFilters re-reads `types` from the URL.
    const clearDrillThrough = () => {
        clearDrillStates();
        setFilterControlSource('all');
        setFilterTypes({ Deals: true, Enterprise: true, DarkWeb: true, Ransomware: true, TPRM: true, AI: true, BusinessUnits: true, GRC: true, External: true });
        const next = new URLSearchParams(searchParams);
        DRILL_PARAMS.forEach(name => next.delete(name));
        setSearchParams(next, { replace: true });
    };

    const toggleTypeFilter = (type) => {
        setFilterTypes(prev => ({ ...prev, [type]: !prev[type] }));
    };

    const resetAllFilters = () => {
        setFilterTypes(getInitialFilterTypes());
        setFilterStatus('all');
        setFilterSeverity('all');
        setFilterFramework('all');
        setFilterDeal('all');
        setFilterApplication('all');
        setFilterVendor('all');
        setFilterBusinessUnit('all');
        setFilterBUApplication('all');
        setFilterAllApplication('all');
        setFilterControlSource('all');
        setFilterRiskCategory('all');
        setFilterRemediation('all');
        setFilterAssignedTo('all');
        setSearchTerm('');
        // Only a drill-through link: a plain ?types= link keeps its type filter and URL on reset.
        if (drillCategories || searchParams.has('category')) clearDrillThrough();
    };

    const [selectedRiskIds, setSelectedRiskIds]         = useState(new Set());
    const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);

    const toggleRiskSelection = (id) =>
        setSelectedRiskIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const toggleSelectAll = () =>
        setSelectedRiskIds(prev =>
            prev.size === filteredRisks.length && filteredRisks.length > 0
                ? new Set()
                : new Set(filteredRisks.map(r => r.id))
        );

    const fetchRisks = async () => {
        try {
            const data = await apiCall('GET', '/allrisks/details');
            setRisks(data || []);
        } catch (err) {} finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRisks();
    }, []);

    // Phase 1: when risks load and a riskId param is present, reset all filters and store highlight target
    useEffect(() => {
        const targetRiskId = searchParams.get('riskId');
        // The URL lost its riskId without an unmount (Risks menu, browser Back): stop showing one risk.
        if (!targetRiskId) {
            setFocusRiskId(null);
            setHighlightRiskId(null);
            return;
        }
        if (risks.length === 0) return;
        setFilterStatus('all');
        setFilterSeverity('all');
        setFilterRemediation('all');
        setSearchTerm('');
        setFilterTypes({ Deals: true, Enterprise: true, DarkWeb: true, Ransomware: true, TPRM: true, AI: true, BusinessUnits: true, GRC: true, External: true });
        setHighlightRiskId(targetRiskId);
        setFocusRiskId(targetRiskId);
    }, [risks, searchParams]);

    // The drill-through params change without an unmount (Risks menu, browser Back or Forward). The
    // states above are seeded from the URL only on mount, so they are re-read here: without this, Back
    // into a drill-through URL after the Risks menu cleared it showed the full, unfiltered list.
    const drillParamsKey = DRILL_PARAMS.map(name => `${name}=${searchParams.getAll(name).join('\u0001')}`).join('&');
    useEffect(() => {
        if (!searchParams.has('category')) {
            if (drillCategories) clearDrillThrough();
            return;
        }
        setDrillCategories(parseCategories(searchParams));
        setDrillRiskType(searchParams.get('riskType'));
        setDrillBlankControlSource(searchParams.get('blankControlSource') === '1');
        setDrillBlankRiskType(searchParams.get('blankRiskType'));
        setFilterControlSource(searchParams.get('controlSource') || 'all');
        setFilterRiskCategory('all');
        setFilterTypes(getInitialFilterTypes());
    }, [drillParamsKey]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await apiCall('GET', '/users');
                setUsers(response || []);
                const mapping = {};
                (response || []).forEach(u => { mapping[String(u.id)] = u.fullName || u.email; });
                setUserMap(mapping);
            } catch (error) {}
        };
        fetchUsers();
    }, []);

    // ── Derived filter option lists ──────────────────────────────────────────

    const uniqueDeals = useMemo(() => {
        const seen = new Set();
        return risks
            .filter(r => r.dealId && r.dealName && !seen.has(r.dealId) && seen.add(r.dealId))
            .map(r => ({ dealId: r.dealId, dealName: r.dealName }))
            .sort((a, b) => a.dealName.localeCompare(b.dealName));
    }, [risks]);

    const uniqueApplications = useMemo(() => {
        return [...new Set(
            risks
                .filter(r => filterDeal === 'all' || String(r.dealId) === String(filterDeal))
                .filter(r => r.applicationName)
                .map(r => r.applicationName)
        )].sort();
    }, [risks, filterDeal]);

    const uniqueVendors = useMemo(() => {
        return [...new Set(risks.filter(r => r.vendorName).map(r => r.vendorName))].sort();
    }, [risks]);

    const uniqueBusinessUnits = useMemo(() => {
        const seen = new Set();
        return risks
            .filter(r => r.businessUnitId && r.businessUnitName && !seen.has(r.businessUnitId) && seen.add(r.businessUnitId))
            .map(r => ({ businessUnitId: r.businessUnitId, businessUnitName: r.businessUnitName }))
            .sort((a, b) => a.businessUnitName.localeCompare(b.businessUnitName));
    }, [risks]);

    const uniqueBUApplications = useMemo(() => {
        return [...new Set(
            risks
                .filter(r => filterBusinessUnit === 'all' || String(r.businessUnitId) === String(filterBusinessUnit))
                .filter(r => r.applicationName)
                .map(r => r.applicationName)
        )].sort();
    }, [risks, filterBusinessUnit]);

    const uniqueAllApplications = useMemo(() => {
        return [...new Set(
            risks
                .filter(r => filterVendor === 'all' || r.vendorName === filterVendor)
                .filter(r => r.applicationName)
                .map(r => r.applicationName)
        )].sort();
    }, [risks, filterVendor]);

    const uniqueControlSources = useMemo(() => {
        return [...new Set(risks.filter(r => r.controlSource).map(r => r.controlSource))].sort();
    }, [risks]);

    const uniqueRiskCategories = useMemo(() => {
        let source = risks.filter(r => r.riskCategory);
        if (filterControlSource !== 'all') {
            source = source.filter(r => r.controlSource === filterControlSource);
        }
        return [...new Set(source.map(r => r.riskCategory))].sort();
    }, [risks, filterControlSource]);

    // Control frameworks are only surfaced for Enterprise, AI, and GRC risks — the framework
    // filter, its options, and the per-risk pills all ignore other risk types' framework data.
    // 'AI' is the AI *assessment* type. AI Intelligence posture risks are RiskType 'AiAsset' in the
    // database, but the DTO collapses every non-assessment type into the 'External' bucket, so
    // 'AiAsset' never reaches the browser — which is why their framework references were invisible.
    // Including 'External' costs nothing for the integration risks that share that bucket: risks
    // with no framework data render no pills.
    const FRAMEWORK_RISK_TYPES = ['Enterprise', 'AI', 'External', 'GRC', 'DarkWeb', 'Ransomware', 'BusinessUnit', 'BusinessUnits', 'TPRM', 'DealApplication', 'Deals'];
    const showsFrameworks = (r) => FRAMEWORK_RISK_TYPES.includes(r.riskType);

    const uniqueFrameworks = useMemo(() => {
        const set = new Set();
        risks.filter(showsFrameworks).forEach(r => (r.affectedFrameworks || []).forEach(f => set.add(f)));
        return [...set].sort();
    }, [risks]);

    // ── Filter + sort logic ──────────────────────────────────────────────────

    const severityOrder = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };

    const filteredRisks = useMemo(() => {
        // Opened from a link to one risk: that risk alone, whatever the filters say.
        if (focusRiskId)
            return risks.filter(r => String(r.id) === String(focusRiskId));

        // A risk matches a type checkbox by its primary riskType OR by any of its tags (cross-listing,
        // e.g. an Enzoic "External" risk tagged DarkWeb + Ransomware). Tag matches are gated to
        // licensed/visible buckets so a hidden checkbox (which defaults on) can't force-include risks.
        let result = risks.filter(r =>
            filterTypes[r.riskType] ||
            (r.tags || []).some(t => licensedFilterTypes.includes(t) && filterTypes[t]));

        // Org-level deep link: only risks with no deal and no business unit.
        if (scopeOrg)
            result = result.filter(r => !r.dealId && !r.businessUnitId);

        // 'active' = everything still in play: any status except Completed/Deferred (incl. risks with
        // no remediation status set yet).
        if (filterStatus === 'active')
            result = result.filter(r => r.remediationStatus !== 'Completed' && r.remediationStatus !== 'Deferred');
        else if (filterStatus !== 'all')
            result = result.filter(r => r.remediationStatus === filterStatus);

        if (filterRemediation === 'active')
            result = result.filter(r => !r.isRemediationCompleted);
        else if (filterRemediation === 'remediated')
            result = result.filter(r => r.isRemediationCompleted);

        if (filterSeverity !== 'all')
            result = result.filter(r => (r.riskSeverity || '').toUpperCase() === filterSeverity);

        if (filterFramework !== 'all')
            result = result.filter(r => showsFrameworks(r) && (r.affectedFrameworks || []).includes(filterFramework));

        if (filterDeal !== 'all')
            result = result.filter(r => String(r.dealId) === String(filterDeal));

        if (filterApplication === 'deal_only')
            result = result.filter(r => !r.applicationName);
        else if (filterApplication === 'apps_only')
            result = result.filter(r => !!r.applicationName);
        else if (filterApplication !== 'all')
            result = result.filter(r => r.applicationName === filterApplication);

        if (filterVendor !== 'all')
            result = result.filter(r => r.vendorName === filterVendor);

        if (filterBusinessUnit !== 'all')
            result = result.filter(r => String(r.businessUnitId) === String(filterBusinessUnit));

        if (filterBUApplication !== 'all')
            result = result.filter(r => r.applicationName === filterBUApplication);

        if (filterAllApplication !== 'all')
            result = result.filter(r => r.applicationName === filterAllApplication);

        if (filterControlSource !== 'all') {
            // A drill-through matches the way the scorecard groups: trimmed and ignoring case.
            const drillSource = filterControlSource.trim().toLowerCase();
            const blankType = drillCategories && drillBlankRiskType ? drillBlankRiskType.toLowerCase() : null;
            result = result.filter(r => drillCategories
                ? (r.controlSource || '').trim().toLowerCase() === drillSource
                    || (blankType != null && !(r.controlSource || '').trim() && (r.unifiedRiskType || '').toLowerCase() === blankType)
                : r.controlSource === filterControlSource);
        }

        if (filterRiskCategory !== 'all')
            result = result.filter(r => r.riskCategory === filterRiskCategory);

        if (drillCategories) {
            result = result.filter(r => drillCategories.has((r.riskCategory ?? '').trim().toLowerCase()));
            if (drillRiskType) {
                const riskType = drillRiskType.toLowerCase();
                result = result.filter(r => (r.unifiedRiskType || '').toLowerCase() === riskType);
            }
            if (drillBlankControlSource)
                result = result.filter(r => !(r.controlSource || '').trim());
        }

        if (filterAssignedTo === 'unassigned')
            result = result.filter(r => !assignedUserIdOf(r));
        else if (filterAssignedTo === 'mine')
            result = result.filter(r => String(assignedUserIdOf(r)) === String(user?.id));
        else if (filterAssignedTo !== 'all')
            result = result.filter(r => String(assignedUserIdOf(r)) === String(filterAssignedTo));

        if (searchTerm) {
            const lc = searchTerm.toLowerCase();
            result = result.filter(r =>
                (r.riskTitle || '').toLowerCase().includes(lc) ||
                (r.riskDescription || '').toLowerCase().includes(lc) ||
                (r.dealName || '').toLowerCase().includes(lc) ||
                (r.vendorName || '').toLowerCase().includes(lc) ||
                (r.businessUnitName || '').toLowerCase().includes(lc) ||
                (r.applicationName || '').toLowerCase().includes(lc)
            );
        }

        result = [...result].sort((a, b) => {
            if (sortBy === 'severity') {
                const aS = severityOrder[(a.riskSeverity || '').toUpperCase()] || 0;
                const bS = severityOrder[(b.riskSeverity || '').toUpperCase()] || 0;
                return sortOrder === 'desc' ? bS - aS : aS - bS;
            }
            if (sortBy === 'financialImpact') {
                const aV = Number(a.finalCalculatedMonetaryImpact ?? 0);
                const bV = Number(b.finalCalculatedMonetaryImpact ?? 0);
                return sortOrder === 'desc' ? bV - aV : aV - bV;
            }
            if (sortBy === 'title') {
                return sortOrder === 'desc'
                    ? (b.riskTitle || '').localeCompare(a.riskTitle || '')
                    : (a.riskTitle || '').localeCompare(b.riskTitle || '');
            }
            return 0;
        });

        return result;
    }, [risks, focusRiskId, filterTypes, licensedFilterTypes, scopeOrg, filterStatus, filterRemediation, filterSeverity, filterFramework, filterDeal,
        filterApplication, filterVendor, filterBusinessUnit, filterBUApplication, filterAllApplication, filterControlSource, filterRiskCategory, drillCategories, drillRiskType, drillBlankControlSource, drillBlankRiskType, filterAssignedTo, user, searchTerm, sortBy, sortOrder]);

    useEffect(() => { setFilterAllApplication('all'); }, [filterVendor]);

    // Reset to page 1 when filter/search/sort criteria change (but NOT on a plain
    // data refetch — e.g. after saving via Manage — so the user stays on their page)
    useEffect(() => { setPage(1); }, [filterTypes, licensedFilterTypes, scopeOrg, filterStatus,
        filterRemediation, filterSeverity, filterFramework, filterDeal, filterApplication, filterVendor,
        filterBusinessUnit, filterBUApplication, filterAllApplication, filterControlSource, filterRiskCategory,
        drillCategories, drillRiskType, drillBlankControlSource, filterAssignedTo, searchTerm, sortBy, sortOrder]);

    const totalPages = Math.max(1, Math.ceil(filteredRisks.length / pageSize));

    // If the list shrank (e.g. a risk was filtered/removed) keep the page in range
    useEffect(() => { setPage(p => Math.min(p, totalPages)); }, [totalPages]);
    const pagedRisks = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredRisks.slice(start, start + pageSize);
    }, [filteredRisks, page, pageSize]);

    // Phase 2: once filteredRisks is updated (after filter reset), navigate to the correct page and scroll
    useEffect(() => {
        if (!highlightRiskId) return;
        const idx = filteredRisks.findIndex(r => String(r.id) === String(highlightRiskId));
        if (idx === -1) return;
        const targetPage = Math.ceil((idx + 1) / pageSize);
        setPage(targetPage);
        setExpandedCards(prev => ({ ...prev, [highlightRiskId]: true }));
        setHighlightRiskId(null); // clear so this runs only once
        setTimeout(() => {
            const el = document.getElementById(`risk-card-${highlightRiskId}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
    }, [filteredRisks, highlightRiskId]);

    // ── Helper functions ─────────────────────────────────────────────────────

    const getTypeColor = (type) => {
        switch (type) {
            case 'Deals':         return 'text-blue-600 bg-blue-100 border-blue-300';
            case 'DarkWeb':       return 'text-purple-600 bg-purple-100 border-purple-300';
            case 'Ransomware':    return 'text-red-600 bg-red-100 border-red-300';
            case 'Enterprise':    return 'text-green-600 bg-green-100 border-green-300';
            case 'TPRM':          return 'text-teal-600 bg-teal-100 border-teal-300';
            case 'AI':            return 'text-amber-600 bg-amber-100 border-amber-300';
            case 'BusinessUnits': return 'text-indigo-600 bg-indigo-100 border-indigo-300';
            case 'GRC':           return 'text-rose-600 bg-rose-100 border-rose-300';
            case 'External':      return 'text-cyan-600 bg-cyan-100 border-cyan-300';
            default:              return 'text-gray-600 bg-gray-100 border-gray-300';
        }
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'Deals':         return 'Deals';
            case 'DarkWeb':       return 'Dark Web';
            case 'Ransomware':    return 'Ransomware';
            case 'Enterprise':    return 'Enterprise';
            case 'TPRM':          return 'TPRM';
            case 'AI':            return 'AI';
            case 'BusinessUnits': return 'Business Units';
            case 'GRC':           return 'GRC';
            case 'External':      return 'External';
            default:              return type || 'Unknown';
        }
    };

    const getSeverityColor = (severity) => {
        switch ((severity || '').toUpperCase()) {
            case 'CRITICAL': return 'text-red-700 bg-red-100 border-red-300';
            case 'HIGH':     return 'text-orange-600 bg-orange-100 border-orange-300';
            case 'MEDIUM':   return 'text-yellow-600 bg-yellow-100 border-yellow-300';
            case 'LOW':      return 'text-green-600 bg-green-100 border-green-300';
            default:         return 'text-gray-600 bg-gray-100 border-gray-300';
        }
    };

    const formatSeverityLabel = (s) => {
        if (!s) return '';
        return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    };


    const handleSortToggle = (field) => {
        if (sortBy === field) {
            setSortOrder(o => o === 'desc' ? 'asc' : 'desc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
        setPage(1);
    };

    // ── Render ───────────────────────────────────────────────────────────────

    if (loading) return <LoadingSpinner />;

    return (
        <>
        <div className="min-h-screen bg-theme-primary">
            <Navigation />
            <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">

                {/* Page header */}
                <h1 className="text-3xl font-bold text-content-primary mb-2">All Risks</h1>
                <p className="text-gray-600 mb-6">View and filter security risks across all modules</p>

                {/* Filters and search do not apply to a single linked risk; they return with "Show all risks". */}
                {!focusRiskId && (<>
                {/* Collapsible filter panel */}
                <div className="bg-white rounded-lg shadow mb-4">
                    <button
                        onClick={() => setFiltersExpanded(!filtersExpanded)}
                        className="w-full px-4 py-2 flex items-center justify-between text-left"
                    >
                        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                            <Filter className="h-4 w-4" />
                            All Filters
                        </span>
                        {filtersExpanded
                            ? <ChevronDown className="h-4 w-4 text-gray-500" />
                            : <ChevronRight className="h-4 w-4 text-gray-500" />}
                    </button>

                    {filtersExpanded && (
                        <div className="px-4 py-3 border-t">
                            {/* Module type checkboxes */}
                            <div className="flex items-center flex-wrap gap-x-6 gap-y-2 mb-3">
                                <span className="text-sm font-medium text-gray-700">Type:</span>
                                {(() => {
                                    const allChecked = licensedFilterTypes.every(t => filterTypes[t]);
                                    return (
                                        <label className="flex items-center space-x-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={allChecked}
                                                onChange={() => {
                                                    const next = !allChecked;
                                                    setFilterTypes(prev => {
                                                        const updated = { ...prev };
                                                        licensedFilterTypes.forEach(t => { updated[t] = next; });
                                                        return updated;
                                                    });
                                                }}
                                                className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                            />
                                            <span className="text-sm font-medium text-gray-500">All</span>
                                        </label>
                                    );
                                })()}
                                {licensedFilterTypes.map(type => (
                                    <label key={type} className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={filterTypes[type]}
                                            onChange={() => toggleTypeFilter(type)}
                                            className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                        />
                                        <span className="text-sm text-gray-700">{getTypeLabel(type)}</span>
                                    </label>
                                ))}
                            </div>

                            {/* Row 1: Status, Severity */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                                    <select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    >
                                        <option value="all">All Statuses</option>
                                        <option value="active">All Active Statuses</option>
                                        <option value="Not Started">Not Started</option>
                                        <option value="Planned">Planned</option>
                                        <option value="In Progress">In Progress</option>
                                        <option value="Under Review">Under Review</option>
                                        <option value="Completed">Completed</option>
                                        <option value="Deferred">Deferred</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Severity</label>
                                    <select
                                        value={filterSeverity}
                                        onChange={(e) => setFilterSeverity(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    >
                                        <option value="all">All Severities</option>
                                        <option value="CRITICAL">Critical</option>
                                        <option value="HIGH">High</option>
                                        <option value="MEDIUM">Medium</option>
                                        <option value="LOW">Low</option>
                                    </select>
                                </div>
                            </div>

                            {/* Row 2: Assigned To, Framework */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Assigned To</label>
                                    <select
                                        value={filterAssignedTo}
                                        onChange={(e) => setFilterAssignedTo(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    >
                                        <option value="all">All</option>
                                        <option value="unassigned">Unassigned</option>
                                        <option value="mine">Assigned to Me</option>
                                        {users
                                            .slice()
                                            .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''))
                                            .map(u => (
                                                <option key={u.id} value={u.id}>{u.fullName} ({u.email})</option>
                                            ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Framework</label>
                                    <select
                                        value={filterFramework}
                                        onChange={(e) => setFilterFramework(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    >
                                        <option value="all">All Frameworks</option>
                                        {uniqueFrameworks.map(f => (
                                            <option key={f} value={f}>{f}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Row 3: Deal + Deal Application (Deals licensed only) */}
                            {hasLicense('Deals') && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Deal</label>
                                        <select
                                            value={filterDeal}
                                            onChange={(e) => { setFilterDeal(e.target.value); setFilterApplication('all'); }}
                                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                        >
                                            <option value="all">All Deals</option>
                                            {uniqueDeals.map(d => (
                                                <option key={d.dealId} value={d.dealId}>{d.dealName}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Deal Application</label>
                                        <select
                                            value={filterApplication}
                                            onChange={(e) => setFilterApplication(e.target.value)}
                                            disabled={filterDeal === 'all'}
                                            className={`w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm ${filterDeal === 'all' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        >
                                            {filterDeal === 'all' ? (
                                                <option value="all">-- Select a Deal --</option>
                                            ) : (
                                                <>
                                                    <option value="deal_only">Only Deal - No Application</option>
                                                    <option value="all">Deals + All Applications</option>
                                                    <option value="apps_only">All Applications</option>
                                                    {uniqueApplications.map(a => (
                                                        <option key={a} value={a}>{a}</option>
                                                    ))}
                                                </>
                                            )}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* Row 4: Business Unit + BU Application (BusinessUnits licensed only) */}
                            {hasLicense('BusinessUnits') && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Business Unit</label>
                                        <select
                                            value={filterBusinessUnit}
                                            onChange={(e) => { setFilterBusinessUnit(e.target.value); setFilterBUApplication('all'); }}
                                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                        >
                                            <option value="all">All Business Units</option>
                                            {uniqueBusinessUnits.map(bu => (
                                                <option key={bu.businessUnitId} value={bu.businessUnitId}>{bu.businessUnitName}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">BU Application</label>
                                        <select
                                            value={filterBUApplication}
                                            onChange={(e) => setFilterBUApplication(e.target.value)}
                                            disabled={filterBusinessUnit === 'all'}
                                            className={`w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm ${filterBusinessUnit === 'all' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        >
                                            <option value="all">{filterBusinessUnit === 'all' ? '-- Select a Business Unit --' : 'All Applications'}</option>
                                            {uniqueBUApplications.map(a => (
                                                <option key={a} value={a}>{a}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* Row 5: Vendor, All Applications */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Vendor</label>
                                    <select
                                        value={filterVendor}
                                        onChange={(e) => setFilterVendor(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    >
                                        <option value="all">All Vendors</option>
                                        {uniqueVendors.map(v => (
                                            <option key={v} value={v}>{v}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">All Applications</label>
                                    <select
                                        value={filterAllApplication}
                                        onChange={(e) => setFilterAllApplication(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    >
                                        <option value="all">All Applications</option>
                                        {uniqueAllApplications.map(a => (
                                            <option key={a} value={a}>{a}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Row: GRC Control Source, GRC Risk Category */}
                            {hasLicense('GRC') && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">GRC Control Source</label>
                                        <select
                                            value={filterControlSource}
                                            onChange={(e) => { setFilterControlSource(e.target.value); setFilterRiskCategory('all'); clearDrillStates(); dropDrillParams(e.target.value); }}
                                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                        >
                                            <option value="all">All Control Sources</option>
                                            {uniqueControlSources.map(s => (<option key={s} value={s}>{s === "RansomwareLiveMonitoring" ? "Ransomware Monitoring" : s}</option>))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">GRC Risk Category</label>
                                        <select
                                            value={filterRiskCategory}
                                            onChange={(e) => { setFilterRiskCategory(e.target.value); clearDrillStates(); dropDrillParams(); }}
                                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                        >
                                            <option value="all">All Risk Categories</option>
                                            {uniqueRiskCategories.map(c => (<option key={c} value={c}>{c}</option>))}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* Row: Remediation + Reset button */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Remediation</label>
                                    <select
                                        value={filterRemediation}
                                        onChange={(e) => setFilterRemediation(e.target.value)}
                                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                                    >
                                        <option value="all">All Risks</option>
                                        <option value="active">Active Only</option>
                                        <option value="remediated">Remediated Only</option>
                                    </select>
                                </div>
                                <div className="flex items-end justify-end">
                                    <button
                                        onClick={resetAllFilters}
                                        className="px-4 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md border border-gray-300 transition-colors"
                                    >
                                        Reset Filters
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Search + Sort bar */}
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    <div className="relative flex-1 min-w-48">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search risks..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 w-full border border-gray-300 rounded-md py-2 text-sm"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Sort by:</span>
                        {[
                            { key: 'severity', label: 'Severity' },
                            { key: 'financialImpact', label: 'Financial Impact' },
                            { key: 'title', label: 'Title' },
                        ].map(({ key, label }) => (
                            <button
                                key={key}
                                onClick={() => handleSortToggle(key)}
                                className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${sortBy === key
                                    ? 'bg-indigo-600 text-white border-indigo-600'
                                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                            >
                                {label} {sortBy === key ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                            </button>
                        ))}
                    </div>
                </div>
                </>)}

                {/* Bulk assign action bar */}
                {selectedRiskIds.size > 0 && (
                    <div className={`sticky ${PINNED_PANEL_TOP} z-20 flex items-center gap-3 mb-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-lg shadow-sm`}>
                        <span className="text-sm font-medium text-blue-800">
                            {selectedRiskIds.size} risk{selectedRiskIds.size !== 1 ? 's' : ''} selected
                        </span>
                        <button
                            onClick={() => setShowBulkAssignModal(true)}
                            className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 flex items-center gap-1.5"
                        >
                            Assign Selected
                        </button>
                        <button onClick={() => setSelectedRiskIds(new Set())} className="text-sm text-blue-600 hover:text-blue-800">Clear</button>
                    </div>
                )}
                {drillCategories && !focusRiskId && (
                    <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800" data-testid="scorecard-drill-banner">
                        <span>
                            Showing risks from the Remediation Scorecard.
                            {scorecardCategory && ` Scorecard category “${scorecardCategory}” shows here under its original categories.`}
                        </span>
                        <button type="button" onClick={clearDrillThrough} className="shrink-0 font-medium text-blue-600 hover:text-blue-800">
                            Dismiss
                        </button>
                    </div>
                )}
                {/* Result count + Select All */}
                <div ref={listTopRef} className="flex items-center justify-between mb-4">
                    {focusRiskId ? (
                        <span className="text-sm text-gray-600 flex items-center gap-2" data-testid="linked-risk-banner">
                            Showing 1 linked risk
                            <button type="button" onClick={showAllRisks} className="text-blue-600 hover:text-blue-800 font-medium">
                                Show all risks
                            </button>
                        </span>
                    ) : (
                    <span className="text-sm text-gray-500">
                        Showing {pagedRisks.length} of {filteredRisks.length} risks
                        {filteredRisks.length !== risks.length && ` (filtered from ${risks.length} total)`}
                    </span>
                    )}
                    {canManageOrgRisks && filteredRisks.length > 0 && (
                        <label className="flex items-center gap-1.5 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                className="h-4 w-4 text-blue-600 rounded border-gray-300 cursor-pointer"
                                checked={selectedRiskIds.size === filteredRisks.length && filteredRisks.length > 0}
                                ref={el => { if (el) el.indeterminate = selectedRiskIds.size > 0 && selectedRiskIds.size < filteredRisks.length; }}
                                onChange={toggleSelectAll}
                            />
                            <span className="text-sm text-gray-600">Select All</span>
                        </label>
                    )}
                </div>

                {/* Risk cards */}
                {pagedRisks.length === 0 ? (
                    focusRiskId ? (
                        <div className="bg-white rounded-lg shadow p-12 text-center">
                            <p className="text-gray-400 text-lg">The linked risk could not be found.</p>
                            <p className="text-gray-400 text-sm mt-1">It may have been deleted, or you may not have access to it.</p>
                        </div>
                    ) : (
                    <div className="bg-white rounded-lg shadow p-12 text-center">
                        <p className="text-gray-400 text-lg">No risks match the current filters.</p>
                        <p className="text-gray-400 text-sm mt-1">Try adjusting or clearing the filters.</p>
                    </div>
                    )
                ) : (
                    <div className="space-y-6">
                        {pagedRisks.map(risk => {
                            const riskKey = String(risk.id);
                            const activeTab = riskActiveTabs[riskKey] || 'overview';
                            const setTab = (tab) =>
                                setRiskActiveTabs(prev => ({ ...prev, [riskKey]: tab }));

                            const assignedName = risk.assignedToUserName || (risk.assignedToUserId ? userMap[String(risk.assignedToUserId)] : null);
                            const approverWaitingId =
                                risk.status === 'AcceptancePendingPrimaryApproval' ? risk.primaryApproverUserId :
                                risk.status === 'AcceptancePendingSecondaryApproval' ? risk.secondaryApproverUserId : null;
                            const approverWaitingName = approverWaitingId ? (userMap[String(approverWaitingId)] || `User #${approverWaitingId}`) : null;

                            return (
                                <div key={riskKey} id={`risk-card-${riskKey}`}>
                                <Card>
                                    {canManageRisk(risk) && (
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 mb-2 text-blue-600 rounded border-gray-300 cursor-pointer"
                                            checked={selectedRiskIds.has(risk.id)}
                                            onChange={e => { e.stopPropagation(); toggleRiskSelection(risk.id); }}
                                        />
                                    )}
                                    {/* ── Card Header ── */}
                                    <div className={`flex justify-between items-start gap-4 ${expandedCards[riskKey] ? 'mb-4' : 'mb-0'}`}>
                                        <div className="flex-1 min-w-0">
                                            {/* Module badge + title + severity + status */}
                                            <div className="flex items-center flex-wrap gap-2 mb-1">
                                                <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${getTypeColor(risk.riskType)}`}>
                                                    {getTypeLabel(risk.riskType)}
                                                </span>
                                                {/* Cross-listing tags (e.g. an External Enzoic risk tagged DarkWeb + Ransomware) */}
                                                {(risk.tags || []).filter(t => t !== risk.riskType).map(t => (
                                                    <span key={t} className={`px-2 py-0.5 rounded text-xs font-medium border ${getTypeColor(t)}`}>
                                                        {getTypeLabel(t)}
                                                    </span>
                                                ))}
                                                <h3 className="text-base font-semibold text-gray-900 leading-tight">
                                                    {risk.riskTitle || 'Untitled Risk'}
                                                </h3>
                                                {risk.riskSeverity && (
                                                    <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${getSeverityColor(risk.riskSeverity)}`}>
                                                        {(risk.riskSeverity || '').toUpperCase()}
                                                    </span>
                                                )}
                                                {risk.remediationStatus && (
                                                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                                                        risk.remediationStatus === 'Completed'    ? 'text-green-600 bg-green-100 border-green-300' :
                                                        risk.remediationStatus === 'In Progress'  ? 'text-blue-600 bg-blue-100 border-blue-300' :
                                                        risk.remediationStatus === 'Under Review' ? 'text-purple-600 bg-purple-100 border-purple-300' :
                                                        risk.remediationStatus === 'Planned'      ? 'text-yellow-600 bg-yellow-100 border-yellow-300' :
                                                        risk.remediationStatus === 'Deferred'     ? 'text-gray-600 bg-gray-100 border-gray-300' :
                                                                                                    'text-gray-600 bg-gray-100 border-gray-300'
                                                    }`}>
                                                        {risk.remediationStatus}
                                                    </span>
                                                )}
                                                {risk.remediationStatus && risk.progressPercentage != null && (
                                                    <span className="text-xs font-medium text-gray-600">{risk.progressPercentage}% complete</span>
                                                )}
                                            </div>

                                            {/* Category + contextual context */}
                                            <div className="text-xs text-gray-500 flex flex-wrap gap-2 mt-1">
                                                {risk.riskCategory && (
                                                    <span>{risk.riskCategory}</span>
                                                )}
                                                {!risk.dealId && !risk.businessUnitId && selectedOrgName && (
                                                    <span className="text-purple-600">· Org: {selectedOrgName}</span>
                                                )}
                                                {risk.dealName && (
                                                    <span className="text-blue-600">· Deal: {risk.dealName}</span>
                                                )}
                                                {risk.applicationName && (
                                                    <span className="text-blue-500">· App: {risk.applicationName}</span>
                                                )}
                                                {risk.vendorName && (
                                                    <span className="text-teal-600">· Vendor: {risk.vendorName}</span>
                                                )}
                                                {risk.businessUnitName && (
                                                    <span className="text-indigo-600">· BU: {risk.businessUnitName}</span>
                                                )}
                                            </div>

                                            {(risk.remediationTargetDate || assignedName || approverWaitingName) && (
                                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 mt-1">
                                                    {risk.remediationTargetDate && (
                                                        <span>
                                                            <span className="font-medium text-gray-700">ETA:</span>{' '}
                                                            {new Date(risk.remediationTargetDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
                                                        </span>
                                                    )}
                                                    {assignedName && (
                                                        <span className="flex items-center gap-1">
                                                            <Shield className="h-3.5 w-3.5 text-indigo-400 shrink-0" />
                                                            <span className="font-medium text-gray-500">Risk Assigned:</span>
                                                            <span className="font-semibold text-indigo-700">{assignedName}</span>
                                                        </span>
                                                    )}
                                                    {approverWaitingName && (
                                                        <span className="text-amber-600">
                                                            <span className="font-medium">Waiting on Approval from:</span> {approverWaitingName}
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            {/* Affected framework pills — Enterprise/AI/GRC risks only */}
                                            {showsFrameworks(risk) && (risk.affectedFrameworks || []).length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {risk.affectedFrameworks.map(f => (
                                                        <span key={f} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded border border-gray-200">
                                                            {f}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                        </div>

                                        {/* Toggle button */}
                                        <button
                                            onClick={() => toggleCard(riskKey)}
                                            className="shrink-0 flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                                            title={expandedCards[riskKey] ? 'Collapse details' : 'Expand details'}
                                        >
                                            {expandedCards[riskKey]
                                                ? <ChevronDown size={18} />
                                                : <ChevronRight size={18} />
                                            }
                                            <span className="text-xs leading-none">{expandedCards[riskKey] ? 'Hide' : 'Details'}</span>
                                        </button>

                                        {/* Financial summary — top right (FAIR-enriched risks only) */}
                                        {risk.financialEngineVersion === 'fair-align-v1.0' && risk.finalCalculatedMonetaryImpact != null && (
                                        <div className="text-right shrink-0">
                                            {risk.impactLow != null && risk.impactHigh != null ? (
                                                <>
                                                    <div
                                                        className="text-lg font-bold cursor-help text-emerald-700"
                                                        title="FAIR-Aligned Expected Annual Loss range — Low to High based on conservative to elevated threat parameters."
                                                    >
                                                        {formatImpact(risk.impactLow)} – {formatImpact(risk.impactHigh)}+
                                                    </div>
                                                    <div className="text-xs text-emerald-600">
                                                        Most Likely: {formatImpact(risk.finalCalculatedMonetaryImpact)}
                                                    </div>
                                                    <div className="text-xs text-gray-400">
                                                        FAIR-Aligned EAL Range
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="text-lg font-bold text-emerald-700">
                                                        {formatImpact(risk.finalCalculatedMonetaryImpact)}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        FAIR-Aligned EAL
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        )}
                                    </div>

                                    {/* ── Three-tab Chrome-style layout (collapsible) ── */}
                                    {expandedCards[riskKey] && (
                                        <RiskDetailTabs
                                            risk={risk}
                                            activeTab={activeTab}
                                            onTabChange={setTab}
                                            grcEnrichState={grcEnrichState}
                                            setGrcEnrichState={setGrcEnrichState}
                                        />
                                    )}
                                    {/* ── End three-tab layout ── */}

                                    {/* ── Action Buttons (always visible, same as Remediation page) ── */}
                                    <RiskWorkflowActions
                                        risk={risk}
                                        onChanged={fetchRisks}
                                        disabledReason={selectedRiskIds.size > 0 ? 'Clear or assign the current selection first' : null}
                                        onAssignSuccess={({ riskId, userId, userName }) => {
                                            setRisks(prev => prev.map(r =>
                                                String(r.id) === String(riskId)
                                                    ? { ...r, assignedToUserId: userId, assignedToUserName: userName, remediationAssignTo: userId ? String(userId) : null }
                                                    : r
                                            ));
                                        }}
                                    />

                                </Card>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Pagination */}
                <Pagination
                    page={page}
                    totalPages={totalPages}
                    totalItems={filteredRisks.length}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    scrollTargetRef={listTopRef}
                    className="mt-6"
                />

            </div>
        </div>
        <>

        {showBulkAssignModal && (() => {
            const selected = filteredRisks.filter(r => selectedRiskIds.has(r.id));
            // Deal context only when every selected risk belongs to the same deal
            const dealIds = new Set(selected.map(r => r.dealId || null));
            const sharedDealId = dealIds.size === 1 ? [...dealIds][0] || undefined : undefined;
            return (
                <BulkAssignRiskModal
                    show={showBulkAssignModal}
                    onClose={() => { setShowBulkAssignModal(false); setSelectedRiskIds(new Set()); }}
                    risks={selected}
                    riskType={null}
                    refreshData={fetchRisks}
                    dealId={sharedDealId}
                />
            );
        })()}
        </>
        </>
    );
};

export default AllRisks;
